import { useMemo, useState } from 'react'
import type { CustomerProfile } from '../../types'
import {
  type AddressBookEntry,
  type AddressSeedProfile,
  formatAddressForProfile,
  normalizeAddressBook,
  toStorageKey,
  writeAddressBookToStorage,
} from './addressBookHelpers'
import { buildAddressLabels, buildCountryLabelMap, buildCountryOptions } from './addressBookConfig'
import {
  buildAddressCandidate,
  removeAddressBookEntry,
  setAddressBookDefaultRole,
  upsertAddressCandidate,
} from './addressBookMutations'
import { useAddressBookPersistence } from './useAddressBookPersistence'
import { useAddressEditor } from './useAddressEditor'

type UseCustomerAddressesControllerParams = {
  profile: CustomerProfile
  language: string
  onUpdateProfile: (payload: {
    residentialAddress?: string
    billingAddress?: string
    shippingAddress?: string
  }) => Promise<boolean>
}

export const useCustomerAddressesController = ({
  profile,
  language,
  onUpdateProfile,
}: UseCustomerAddressesControllerParams) => {
  const isItalian = language === 'it'

  const labels = useMemo(() => buildAddressLabels(isItalian), [isItalian])
  const countryOptions = useMemo(() => buildCountryOptions(isItalian), [isItalian])
  const countryLabelMap = useMemo(() => buildCountryLabelMap(countryOptions), [countryOptions])

  const seedProfile = useMemo<AddressSeedProfile>(
    () => ({
      firstName: profile.firstName,
      lastName: profile.lastName,
      phone: profile.phone,
      residentialAddress: profile.residentialAddress,
      billingAddress: profile.billingAddress,
      shippingAddress: profile.shippingAddress,
    }),
    [
      profile.billingAddress,
      profile.firstName,
      profile.lastName,
      profile.phone,
      profile.residentialAddress,
      profile.shippingAddress,
    ],
  )

  const storageKey = useMemo(() => toStorageKey(profile), [profile])
  const { addressBook, setAddressBook } = useAddressBookPersistence({
    storageKey,
    seedProfile,
    countryFallback: 'IT',
  })
  const {
    editorOpen,
    editingAddressId,
    editorState,
    setEditorState,
    formError,
    setFormError,
    resetEditor,
    startCreateAddress,
    startEditAddress,
  } = useAddressEditor({
    profile,
  })
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')

  const syncProfileFromAddressBook = async (entries: AddressBookEntry[]) => {
    const defaultShipping = entries.find((entry) => entry.defaultShipping) ?? entries.find((entry) => entry.useShipping) ?? null
    const defaultBilling = entries.find((entry) => entry.defaultBilling) ?? entries.find((entry) => entry.useBilling) ?? null
    const residential =
      entries.find((entry) => !entry.useShipping && !entry.useBilling) ?? defaultShipping ?? defaultBilling ?? entries[0] ?? null

    const toProfileValue = (entry: AddressBookEntry | null) =>
      entry ? formatAddressForProfile(entry, entry.country) : ''

    setSaving(true)
    setNotice('')
    try {
      const ok = await onUpdateProfile({
        shippingAddress: toProfileValue(defaultShipping),
        billingAddress: toProfileValue(defaultBilling),
        residentialAddress: toProfileValue(residential),
      })
      setNotice(ok ? labels.saveSuccess : labels.saveError)
    } finally {
      setSaving(false)
    }
  }

  const applyAddressBookUpdate = async (nextAddressBook: AddressBookEntry[]) => {
    const normalized = normalizeAddressBook(nextAddressBook)
    setAddressBook(normalized)
    writeAddressBookToStorage(storageKey, normalized)
    await syncProfileFromAddressBook(normalized)
  }

  const setDefaultRole = async (entryId: string, role: 'shipping' | 'billing') => {
    if (saving) return

    const next = setAddressBookDefaultRole({
      addressBook,
      entryId,
      role,
    })

    await applyAddressBookUpdate(next)
  }

  const removeAddress = async (entryId: string) => {
    if (saving) return
    if (typeof window !== 'undefined' && !window.confirm(labels.removeConfirm)) {
      return
    }

    const next = removeAddressBookEntry(addressBook, entryId)
    await applyAddressBookUpdate(next)
  }

  const submitEditor = async () => {
    const candidateResult = buildAddressCandidate({
      editorState,
      editingAddressId,
      addressBook,
    })

    if (candidateResult.kind !== 'ok') {
      setFormError(candidateResult.kind === 'required_error' ? labels.requiredError : labels.duplicateError)
      return
    }

    const next = upsertAddressCandidate({
      addressBook,
      candidate: candidateResult.candidate,
      editingAddressId,
    })

    await applyAddressBookUpdate(next)
    resetEditor()
  }

  return {
    isItalian,
    labels,
    countryOptions,
    countryLabelMap,
    addressBook,
    editorOpen,
    editingAddressId,
    editorState,
    setEditorState,
    saving,
    notice,
    formError,
    resetEditor,
    startCreateAddress,
    startEditAddress,
    submitEditor,
    removeAddress,
    setDefaultRole,
  }
}
